import React from 'react';

/**
 * LoadingScreen — the first second of the app.
 *
 * Visual intent: a quiet editorial moment, not a spinner.
 * - Centred monogram that fades and rises into place.
 * - Three concentric rings that breathe in the brand 4s rhythm.
 * - A single eyebrow label below, letter-spaced like a caption card.
 *
 * Everything is built from CSS variables so the screen retints with
 * the active theme (Rose / Sage / Twilight). Honours
 * prefers-reduced-motion via the shared utility classes in motion.css.
 */
export default function LoadingScreen({ label }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label || 'Loading'}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 28,
        height: '100%',
        width: '100%',
        background: 'var(--bg)',
        overflow: 'hidden',
      }}
    >
      {/* Soft ambient halo behind the rings — gives the whole screen a
          low hum of warmth so the background never reads as flat. */}
      <span
        aria-hidden="true"
        className="motion-halo"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 280,
          height: 280,
          marginLeft: -140,
          marginTop: -160,
          borderRadius: '50%',
          background:
            'radial-gradient(closest-side, var(--primary-pale), transparent 70%)',
          filter: 'blur(4px)',
          pointerEvents: 'none',
        }}
      />

      {/* Rings + logo group. The group uses --stagger-i so the
          logo rises a beat after the rings settle. */}
      <div
        className="motion-rise"
        style={{
          position: 'relative',
          width: 128,
          height: 128,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '--stagger-i': 0,
        }}
      >
        <span
          className="motion-rings"
          aria-hidden="true"
          style={{ width: 128, height: 128 }}
        >
          <span className="motion-rings__ring" />
          <span className="motion-rings__ring motion-rings__ring--mid" />
          <span className="motion-rings__ring motion-rings__ring--inner" />
        </span>
        {/* <picture> with sized WebP variants — the brand stamp shows
            on every cold load before the React tree paints, so it
            sits squarely on the LCP critical path. logo-96.webp is
            ~1 KB vs logo.png at 16 KB. */}
        <picture>
          <source
            type="image/webp"
            srcSet="/logo-96.webp 96w, /logo-192.webp 192w"
            sizes="64px"
          />
          <img
            aria-hidden="true"
            src="/logo.png"
            alt=""
            width="64"
            height="64"
            style={{
              position: 'absolute',
              width: 64,
              height: 'auto',
              display: 'block',
            }}
          />
        </picture>
      </div>

      {label && (
        <div
          className="motion-rise"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            '--stagger-i': 2,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-title)',
              fontStyle: 'italic',
              fontSize: 18,
              fontWeight: 400,
              color: 'var(--text)',
              letterSpacing: '-0.005em',
              fontVariationSettings: '"opsz" 48',
            }}
          >
            {label}
          </span>
        </div>
      )}
    </div>
  );
}
